import { displayPhone, priceLabel } from '@tangazo/shared';
import { fileUrl } from '@/api/client';
import type { Order } from '@/api/types';
import type { AdOptions, AdText } from './render';

const cache = new Map<string, Promise<HTMLImageElement>>();
export function loadImage(src: string): Promise<HTMLImageElement> {
  if (!cache.has(src)) {
    cache.set(
      src,
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => {
          cache.delete(src);
          reject(new Error('image'));
        };
        img.src = src;
      }),
    );
  }
  return cache.get(src)!;
}

export async function adImages(o: Order, { useAi = false } = {}) {
  const main = useAi && o.aiPhoto?.status === 'done' && o.aiPhoto.use && o.aiPhoto.file ? o.aiPhoto.file : o.photos[0];
  const [photo, logo, ...extra] = await Promise.all([
    main ? loadImage(fileUrl(o.token, main)) : null,
    o.logo ? loadImage(fileUrl(o.token, o.logo)).catch(() => null) : null,
    ...o.photos.slice(1).map((p) => loadImage(fileUrl(o.token, p)).catch(() => null)),
  ]);
  return { photo, logo, extraPhotos: extra.filter((x): x is HTMLImageElement => !!x) };
}

export function adText(o: Order): AdText {
  const opt = o.copy?.options?.[o.design.option || 0];
  const own = o.design.text || {};
  return {
    headline: own.headline ?? opt?.headline ?? '',
    subline: own.subline ?? opt?.subline ?? '',
    offer: own.offer ?? opt?.offer ?? '',
    cta: own.cta ?? opt?.cta ?? '',
  };
}

export function adOptions(o: Order, images: Awaited<ReturnType<typeof adImages>>, tAd: (k: string) => string, extra: Partial<AdOptions> = {}): AdOptions {
  const d = o.details;
  return {
    template: o.design.template || 'bango',
    format: o.design.format || 'status',
    accent: o.design.accent || '#E4572E',
    text: adText(o),
    business: d.business,
    location: d.location,
    price: priceLabel(d.price),
    phone: displayPhone(d.phone),
    labels: { call: tAd('ad.call'), offerWord: tAd('ad.offer_word') },
    ...images,
    ...extra,
  };
}
